import { useCallback, useEffect, useRef, useState } from 'react';

const FEEDBACK_MS = 2200;

interface Clipboard {
  /** The value most recently copied, or null once the feedback has faded. */
  copied: string | null;
  /** Set when copying failed, so the UI can tell the person to select it by hand. */
  failed: boolean;
  copy(value: string): Promise<void>;
}

export function useClipboard(): Clipboard {
  const [copied, setCopied] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = useCallback(async (value: string) => {
    clearTimeout(timer.current);

    try {
      await navigator.clipboard.writeText(value);
      setFailed(false);
      setCopied(value);
    } catch {
      // Insecure contexts and older mobile browsers refuse the async API.
      setFailed(!legacyCopy(value));
      setCopied(value);
    }

    timer.current = setTimeout(() => {
      setCopied(null);
      setFailed(false);
    }, FEEDBACK_MS);
  }, []);

  return { copied, failed, copy };
}

function legacyCopy(value: string): boolean {
  const area = document.createElement('textarea');
  area.value = value;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();

  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(area);
  }
}
